"use client";

import { ChevronLeft, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { StudyDocument, StudyNotUploadedDocument } from "../../../types";
import { getSourceIcon } from "../course-source-list";
import { formatFileSize } from "../../../hooks/useUserLimits";
import { FileViewer } from "./file-viewer";
import { useEffect, useState } from "react";
import { useNotebookContext } from "../../../hooks/useNotebook";

interface SourceViewerProps {
  document: StudyDocument;
  onBack: () => void;
}

export function getFileMimeType(document: StudyDocument) {
  if ("file" in document && document.file) return document.file.type;
  if (document.metadata) {
    if (
      "url" in document.metadata &&
      typeof document.metadata["url"] === "string"
    )
      return "url";
    if (
      "content_type" in document.metadata &&
      typeof document.metadata["content_type"] === "string"
    )
      return document.metadata["content_type"];
  }

  return "";
}

export function getFileSizeText(document: StudyDocument) {
  return "size" in document.metadata
    ? formatFileSize(document.metadata.size as number)
    : "size_text" in document.metadata
    ? (document.metadata.size_text as string)
    : "-";
}

function parseFileSize(text: string): number {
  const trimmed = text.trim();

  if (!trimmed) {
    throw new Error("Invalid file size");
  }

  const match = trimmed.match(/^([\d.]+)\s*(b|bytes|kb|mb|gb)$/i);
  if (!match) {
    throw new Error(`Invalid file size format: "${text}"`);
  }

  const value = parseFloat(match[1]);
  const unit = match[2].toLowerCase();

  if (Number.isNaN(value)) {
    throw new Error(`Invalid numeric value in file size: "${text}"`);
  }

  const multipliers: Record<string, number> = {
    b: 1,
    bytes: 1,
    kb: 1024,
    mb: 1024 ** 2,
    gb: 1024 ** 3,
  };

  return Math.round(value * multipliers[unit]);
}

export function getFileSizeNumeric(
  document: StudyDocument | StudyNotUploadedDocument
) {
  if ("size" in document.metadata && typeof document.metadata.size === "number")
    return document.metadata.size;
  if (
    "size_text" in document.metadata &&
    typeof document.metadata.size_text === "string"
  )
    return parseFileSize(document.metadata.size_text);
  return 0;
}

export function SourceViewer({ document, onBack }: SourceViewerProps) {
  const mime = getFileMimeType(document);
  const Icon = getSourceIcon(mime);
  const size = getFileSizeText(document);

  const { getSourceUrl } = useNotebookContext();
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        setUrl(await getSourceUrl(document));
      } catch {
        setError("Failed to retrieve source. Try reloading the page!");
      }
    })();
  }, [document]);

  return (
    <div className="flex h-full w-full flex-col bg-theme-0">
      {/* Header */}
      <div className="relative bg-theme-0 backdrop-blur-sm z-10">
        <div className="absolute inset-0" />

        <div className="relative flex items-center justify-between h-16 pr-6">
          <div className="flex items-center gap-1 h-16">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="gap-1 text-sm hover:underline text-theme-0"
            >
              <ChevronLeft className="h-4 w-4" />
              Close source
            </Button>

            <Separator
              orientation="vertical"
              className="mr-3 data-[orientation=vertical]:h-5"
            />

            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-theme-1 border border-theme">
                <Icon className="h-5 w-5 text-theme-1" />
              </div>
              <div>
                <div className="text-sm font-bold text-theme-0">
                  {document.name}
                </div>
                <div className="text-xs text-theme-1 font-medium">{size}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Separator />

      {/* Content */}
      <div className="flex-1 overflow-auto">
        <div className="mx-auto max-w-7xl px-6 py-8">
          {/* Hero section */}
          <div className="mb-10">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold text-theme-0 mb-2">
                  {document.name}
                </h2>
              </div>
            </div>
          </div>
          <FileViewer url={url} fileType={mime} error={error} />
        </div>
      </div>
    </div>
  );
}
